import { ISplitActionHandler, SplitActionResult } from "./base";
import {
  NormalizedDeliverySelection,
  EnrichedSupplierForDelivery,
} from "../../../../../components/DeliveryOptions/types";
import { CreateManualDeliveryOptionPayload } from "../../../types/estimateConfirmationTypes";
import { extractApiErrorMessage } from "../../../../../components/DeliveryOptions/utils/apiErrorUtils";
import services from "../../../services";

/**
 * Handles the confirmation flow for estimate splits.
 *
 * For each confirm selection:
 * 1. If manual → creates a manual delivery option via API to get a deliveryOptionId
 * 2. Confirms the split via API with the deliveryOptionId
 *
 * Errors from the backend are surfaced using extractApiErrorMessage.
 */
export class SplitConfirmHandler implements ISplitActionHandler {
  async execute(
    estimateNo: string,
    selections: NormalizedDeliverySelection[],
    splitData: EnrichedSupplierForDelivery[],
  ): Promise<SplitActionResult[]> {
    const confirmSelections = selections.filter(
      (s) => s.customerAction === "confirm",
    );

    const outcomes = await Promise.allSettled(
      confirmSelections.map((selection) =>
        this.processSplitWorkflow(estimateNo, selection, splitData),
      ),
    );

    const results: SplitActionResult[] = outcomes.map((outcome) => {
      if (outcome.status === "fulfilled") {
        return outcome.value;
      } else {
        return {
          supplierId: null,
          success: false,
          error: String(outcome.reason),
        };
      }
    });

    return results;
  }

  private async processSplitWorkflow(
    estimateNo: string,
    selection: NormalizedDeliverySelection,
    splitData: EnrichedSupplierForDelivery[],
  ): Promise<SplitActionResult> {
    const supplierSplit = splitData.find(
      (s) => s.splitId === selection.splitId,
    );
    const supplierDisplayName =
      supplierSplit?.supplierName || "remaining items";

    try {
      let deliveryOptionId = selection.deliveryOptionId;

      // STEP 1: Create manual delivery option if needed
      if (selection.selectionSource === "manual") {
        const payload: CreateManualDeliveryOptionPayload = {
          cptMin: selection.cptMin!,
          cptMax: selection.cptMax!,
          cptSlab: selection.cptSlab!,
          deliveryCost: selection.deliveryCost!,
        };
        const manualResponse =
          await services.createManualDeliveryOptionForEstimateSplit(
            estimateNo,
            selection.splitId,
            payload,
          );
        deliveryOptionId = manualResponse.deliveryOption._id;
      }

      if (!deliveryOptionId) {
        return {
          supplierId: selection.supplierId,
          supplierName: supplierDisplayName,
          success: false,
          error: `Missing deliveryOptionId for ${supplierDisplayName}`,
        };
      }

      // STEP 2: Confirm the split
      const confirmResponse = await services.confirmEstimateSplit(
        estimateNo,
        selection.splitId,
        { deliveryOptionId },
      );

      // STEP 3: done
      return {
        supplierId: selection.supplierId,
        supplierName: supplierDisplayName,
        success: true,
        order: confirmResponse,
      };
    } catch (error: unknown) {
      const errorMessage = extractApiErrorMessage(
        error,
        `Failed to confirm split for ${supplierDisplayName}`,
      );
      return {
        supplierId: selection.supplierId,
        supplierName: supplierDisplayName,
        success: false,
        error: errorMessage,
      };
    }
  }
}
